export type LayoutCurrencyOption = {
  displayName: string
  machineName: string
}

const ISO_CURRENCY_CODE = /^[A-Z]{3}$/i

const normalizeCurrencyCode = (code: string): string | null => {
  const upper = code.toUpperCase()
  return ISO_CURRENCY_CODE.test(upper) ? upper : null
}

const parseCurrencyFromDisplay = (display: string): string | null => {
  const match = display.match(/\b([A-Za-z]{3})\b/)
  return match ? normalizeCurrencyCode(match[1]) : null
}

/** Resolve an ISO 4217 currency code from a Contentful currencyList item. */
export function resolveLayoutCurrencyCode(item: unknown): string | null {
  if (!item || typeof item !== "object") return null

  const { key, value, machineName, displayName } = item as Record<
    string,
    unknown
  >

  for (const candidate of [key, machineName]) {
    if (typeof candidate === "string") {
      const code = normalizeCurrencyCode(candidate)
      if (code) return code
    }
  }

  for (const display of [value, displayName]) {
    if (typeof display === "string") {
      const fromDisplay = parseCurrencyFromDisplay(display)
      if (fromDisplay) return fromDisplay
    }
  }

  return null
}

export function getLayoutCurrencyListItems(
  currencyList: unknown,
): unknown[] {
  if (!currencyList) return []
  if (Array.isArray(currencyList)) return currencyList
  if (typeof currencyList === "object") {
    return Object.values(currencyList as Record<string, unknown>)
  }
  return []
}

/** Map Contentful currencyList → LayoutPricingCards dropdown options. */
export function formatLayoutCurrencyList(
  currencyList: unknown,
): LayoutCurrencyOption[] | null {
  const options = getLayoutCurrencyListItems(currencyList)
    .map((item) => {
      const displayName =
        (item as { value?: string; displayName?: string })?.value ??
        (item as { displayName?: string })?.displayName
      const machineName = resolveLayoutCurrencyCode(item)

      if (!displayName || !machineName) return null

      return {
        displayName: String(displayName),
        machineName,
      }
    })
    .filter((option): option is LayoutCurrencyOption => option != null)

  return options.length ? options : null
}

export function getLayoutCurrencyCodes(
  currencyList: unknown,
  activeCurrency?: string,
): string[] {
  const codes = new Set<string>()

  for (const item of getLayoutCurrencyListItems(currencyList)) {
    const code = resolveLayoutCurrencyCode(item)
    if (code) codes.add(code)
  }

  if (activeCurrency) {
    const normalized = normalizeCurrencyCode(activeCurrency)
    if (normalized) codes.add(normalized)
  }

  if (codes.size) return [...codes]
  if (activeCurrency) {
    const normalized = normalizeCurrencyCode(activeCurrency)
    if (normalized) return [normalized]
  }

  return ["USD"]
}
